import React, { useState, useEffect } from 'react'
import { HandCoins, Calculator, TrendingUp, TrendingDown, Save, Loader2, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'

const API_URL = process.env.REACT_APP_BACKEND_URL

const FORFAITS = {
  IRSA: { label: 'IRSA', montant: 2030, description: 'Indemnisation Regle par le Systeme Automobile' },
  IRCA: { label: 'IRCA', montant: 1600, description: 'Indemnisation Regle par la Convention Automobile' },
  DROIT_COMMUN: { label: 'Droit commun', montant: 0, description: 'Hors convention — charge sinistre = reel TTC' },
}

const fmt = (v) => {
  if (v == null || isNaN(v)) return '\u2014'
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(v)
}

export default function ReglementAmiableBlock({ sinistre, onRefresh }) {
  const [type, setType] = useState(sinistre?.reglement_amiable_type || '')
  const [forfait, setForfait] = useState(sinistre?.forfait_irsa || '')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setType(sinistre?.reglement_amiable_type || '')
    setForfait(sinistre?.forfait_irsa || '')
  }, [sinistre?.id, sinistre?.reglement_amiable_type, sinistre?.forfait_irsa])

  const modeGestion = sinistre?.mode_gestion || (sinistre?.est_amiable ? 'amiable' : null)
  if (modeGestion !== 'amiable') return null

  function handleTypeChange(newType) {
    setType(newType)
    if (FORFAITS[newType]) {
      setForfait(FORFAITS[newType].montant)
    }
  }

  const isDroitCommun = type === 'DROIT_COMMUN'
  const forfaitNum = parseFloat(forfait) || 0
  const montantHT = parseFloat(sinistre?.montant_amiable_ht) || 0
  const hasMontantHT = sinistre?.montant_amiable_ht != null && sinistre?.montant_amiable_ht !== ''
  const economie = isDroitCommun ? null : (forfaitNum - montantHT)
  const hasData = type && (isDroitCommun || forfaitNum > 0)

  async function handleSave() {
    if (!type) {
      toast.error('Selectionnez le type (IRSA, IRCA ou Droit commun)')
      return
    }
    if (!isDroitCommun && !hasMontantHT) {
      toast.error('Le Montant HT doit etre renseigne dans les Informations du reglement ci-dessus')
      return
    }
    setSaving(true)
    try {
      const body = isDroitCommun
        ? { reglement_amiable_type: type, convention: 'droit_commun', forfait_irsa: null }
        : { forfait_irsa: forfaitNum, reglement_amiable_type: type, convention: type.toLowerCase() }
      const r = await fetch(`${API_URL}/api/sinistres/${sinistre.id}/economie-amiable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!r.ok) throw new Error('Erreur serveur')
      toast.success('Reglement amiable enregistre')
      onRefresh()
    } catch (e) {
      toast.error(e.message)
    } finally {
      setSaving(false)
    }
  }

  const isPositive = economie >= 0

  return (
    <div className="bg-teal-50/50 border border-teal-200 rounded-xl p-4 space-y-4" data-testid="reglement-amiable-block">
      <div className="flex items-center gap-2">
        <HandCoins size={16} className="text-teal-600" />
        <h3 className="text-sm font-semibold text-teal-800">Reglement amiable</h3>
      </div>

      {/* Forfait selector with 3 options */}
      <div data-testid="forfait-type-selector">
        <label className="block text-xs font-medium text-teal-700 mb-1.5">Convention / Forfait applicable</label>
        <div className="grid grid-cols-3 gap-2">
          {Object.entries(FORFAITS).map(([key, cfg]) => (
            <button
              key={key}
              onClick={() => handleTypeChange(key)}
              className={`px-3 py-2 rounded-lg border text-left transition-all ${
                type === key
                  ? 'border-teal-500 bg-teal-100 ring-1 ring-teal-400'
                  : 'border-slate-200 bg-white hover:border-teal-300'
              }`}
              data-testid={`forfait-${key.toLowerCase()}-btn`}
            >
              <p className={`text-sm font-semibold ${type === key ? 'text-teal-800' : 'text-slate-700'}`}>{cfg.label}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">{cfg.montant > 0 ? `${fmt(cfg.montant)} (defaut)` : 'Reel TTC'}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Droit commun info */}
      {isDroitCommun && (
        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="droit-commun-info">
          <AlertTriangle size={14} className="text-blue-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-xs font-medium text-blue-800">Droit commun</p>
            <p className="text-[10px] text-blue-600 mt-0.5">Pas de convention forfaitaire. La charge sinistre correspond au montant reel TTC (HT x 1,20).</p>
          </div>
        </div>
      )}

      {/* Montants: Forfait modifiable + Montant HT en lecture seule (masque pour droit commun) */}
      {type && !isDroitCommun && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-teal-700 mb-1">Forfait {type}</label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                value={forfait}
                onChange={e => setForfait(e.target.value)}
                className="w-full px-3 py-2 border border-teal-200 rounded-lg text-sm bg-white focus:ring-1 focus:ring-teal-400 focus:border-teal-400"
                placeholder={String(FORFAITS[type]?.montant || 0)}
                data-testid="forfait-montant-input"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">EUR</span>
            </div>
            <p className="text-[10px] text-slate-400 mt-0.5">Modifiable si le bareme change</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-teal-700 mb-1">Montant paye (garage/tiers HT)</label>
            <div className={`px-3 py-2 border rounded-lg text-sm ${
              hasMontantHT ? 'border-teal-200 bg-teal-50/50 text-slate-800 font-medium' : 'border-amber-300 bg-amber-50 text-amber-600'
            }`}>
              {hasMontantHT ? fmt(montantHT) : (
                <span className="flex items-center gap-1 text-xs">
                  <AlertTriangle size={10} />
                  Renseigner le Montant HT ci-dessus
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Resultat economie (masque pour droit commun) */}
      {type && !isDroitCommun && hasMontantHT && forfaitNum > 0 && (
        <div className={`flex items-center justify-between p-3 rounded-lg border ${
          isPositive ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
        }`} data-testid="economie-result">
          <div className="flex items-center gap-2">
            <Calculator size={14} className={isPositive ? 'text-emerald-600' : 'text-red-600'} />
            <div>
              <p className="text-xs font-medium text-slate-700">
                {isPositive ? 'Economie realisee' : 'Perte sur ce dossier'}
              </p>
              <p className="text-[10px] text-slate-500">
                {fmt(forfaitNum)} (forfait {type}) - {fmt(montantHT)} (montant HT)
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {isPositive ? (
              <TrendingDown size={16} className="text-emerald-600" />
            ) : (
              <TrendingUp size={16} className="text-red-600" />
            )}
            <span className={`text-lg font-bold tabular-nums ${isPositive ? 'text-emerald-700' : 'text-red-700'}`}>
              {isPositive ? '+' : ''}{fmt(economie)}
            </span>
          </div>
        </div>
      )}

      {/* Bouton sauvegarder */}
      {type && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving || !hasData || !hasMontantHT}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="save-reglement-amiable-btn"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Enregistrer
          </button>
        </div>
      )}
    </div>
  )
}
